/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../../../core/device';
import { InteractiveComponents } from '../../../../core/enums';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Log, QoEAppLogger, VuLogger } from '../../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../../core/navigation/ScreenTaskControllerTypes';
import type { TivoPayloadAction } from '../../../../core/tivo/tivo-redux-wrapper';
import { VideoPlaybackController } from '../../../../core/videoplayer/VideoPlaybackController';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../../interactivelayer/interactiveLayer.reducers';
import {
    setScreenTaskController,
    showGuideScreen,
    showUserPreferencesScreen,
} from '../../../screens/screens.reducers';
import {
    userPrefVideoWindowEnableGhostModeAction,
    userPrefVideoWindowSetSelectedOptionAction,
} from './userprefvideowindow.actions';
import {
    USER_PREF_VIDEO_WINDOW_SET_GHOST_MODE,
    USER_PREF_VIDEO_WINDOW_SET_SELECTED_OPTION,
} from './userprefvideowindow.types';
import { UserPrefVideoWindowState } from './userprefvideowindowstate';

const TAG = 'UserPrefVideoWindow';
export const USER_PREF_VIDEO_WINDOW_BUTTON_ID = 'userpref-video-window-button';

const initialState: {
    isGhostModeActive: boolean;
    selectedOption: number;
} = {
    isGhostModeActive: false,
    selectedOption: 0,
};

const userPrefVideoWindowReducer = (
    state = initialState,
    { type, payload } = <
        TivoPayloadAction<{
            isGhostModeActive: boolean;
            selectedOption: number;
        }>
    >{},
) => {
    switch (type) {
        case USER_PREF_VIDEO_WINDOW_SET_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        case USER_PREF_VIDEO_WINDOW_SET_GHOST_MODE:
            return {
                ...state,
                isGhostModeActive: payload.isGhostModeActive,
            };
        default:
            return state;
    }
};

export const onKeyBackUserPrefVideoWindowOperations = () => (dispatch: Function) => {
    dispatch(showUserPreferencesScreen());
};

export const onKeyGuideUserPrefVideoWindowOperations = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
};

export const onKeyLeftUserPrefVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { selectedOption } = getState().userPrefVideoWindow;
        if (selectedOption === UserPrefVideoWindowState.Yes)
            dispatch(userPrefVideoWindowSetSelectedOptionAction(UserPrefVideoWindowState.No));
        else dispatch(userPrefVideoWindowSetSelectedOptionAction(UserPrefVideoWindowState.Yes));
    };

export const onKeyOkUserPrefVideoWindowOperations =
    () => async (dispatch: Function, getState: Function) => {
        const appSettings = DeviceModel.getAppSettings();
        const { selectedOption } = getState().userPrefVideoWindow;
        const playerInstance = VideoPlaybackController.getInstance();
        appSettings.setIsVideoWindowEnabled(!selectedOption);
        try {
            const services = new ServicesClientImpl();

            // Playback should only start when the user enables the video window (transitioning
            // from Off to On). To ensure it is an Off-to-On transition and not On-to-On, we need
            // to verify that the player is not already playing.
            const shouldStartPlayback =
                !playerInstance.isPlaying() && appSettings.isVideoWindowEnabled();
            if (shouldStartPlayback) {
                const playable = await services.getLastPlayedOrFallbackToFirstByChannelNumber(
                    appSettings.getLastTunedChannelNumber(),
                );
                playerInstance.mount();
                playerInstance.startPlayback(playable);
            }

            // We need to stop playback when the user disables the video window (transitioning
            // from On to Off). To ensure it is an On-to-Off transition and not Off-to-Off, we need
            // to verify that the player is already playing.
            const shouldHideVideoWindow =
                playerInstance.isPlaying() && !appSettings.isVideoWindowEnabled();
            if (shouldHideVideoWindow) {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_CLOSED_THE_VIDEO_WINDOW,
                );
                QoEAppLogger.logPlaybackStopped(
                    playerInstance.getPlayerStats(),
                    playerInstance.getPlayerCurrentTime(),
                    playerInstance.getSeekRange(),
                );
                playerInstance.unmount();
                VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VIDEO_WINDOW_OFF);
            }
        } catch (blunder) {
            Log.d(TAG, 'onKeyOkUserPrefVideoWindowOperations ', blunder);
        }
        dispatch(showUserPreferencesScreen());
    };

export const onKeyRightUserPrefVideoWindowOperations = () => (dispatch: Function) => {
    dispatch(onKeyLeftUserPrefVideoWindowOperations());
};

export const onMouseClickInteractiveLayerOperations =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        const { value } = getState().inputFactory;
        if (
            screenTaskController === ScreenTaskControllerTypes.USER_PREF_INTERACTIVE_LAYER_TASKS &&
            (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST)
        ) {
            dispatch(onKeyLeftUserPrefVideoWindowOperations());
        } else if (value === USER_PREF_VIDEO_WINDOW_BUTTON_ID) {
            dispatch(onKeyOkUserPrefVideoWindowOperations());
        }
    };

export const onMouseOutUserPrefVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        const { previousScreenTaskController } = getState().screens;
        dispatch(userPrefVideoWindowEnableGhostMode());
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setScreenTaskController(previousScreenTaskController));
            dispatch(clearActiveInteractiveComponent());
        }
    };

export const onMouseOverUserPrefVideoWindowOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setActiveInteractiveComponent(value));
            dispatch(
                setScreenTaskController(
                    ScreenTaskControllerTypes.USER_PREF_INTERACTIVE_LAYER_TASKS,
                ),
            );
        } else if (value === USER_PREF_VIDEO_WINDOW_BUTTON_ID) {
            dispatch(userPrefVideoWindowDisableGhostMode());
        } else {
            dispatch(userPrefVideoWindowEnableGhostMode());
        }
    };

export const userPrefVideoWindowDisableGhostMode = () => (dispatch: Function) => {
    dispatch(userPrefVideoWindowEnableGhostModeAction(false));
};

export const userPrefVideoWindowEnableGhostMode = () => (dispatch: Function) => {
    dispatch(userPrefVideoWindowEnableGhostModeAction(true));
};

export default userPrefVideoWindowReducer;
